import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { TID } from "../../lib/testIds";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const wordReveal = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const line1 = ["Build", "Smarter", "Software."];
const line2 = ["Scale", "Without", "Engineering", "Bottlenecks."];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid={TID.heroSection}
      className="relative overflow-hidden bg-[#050505] pt-16 md:pt-24 pb-20 md:pb-32"
    >
      {/* Backdrop layers */}
      <div className="absolute inset-0 bg-grid opacity-40" aria-hidden="true" />
      <div className="absolute inset-0 radial-spotlight" aria-hidden="true" />
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[140px] opacity-30"
        style={{ background: "#FF5F1F" }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8 md:mb-12"
        >
          <div className="h-px w-8 bg-[#FF5F1F]" />
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
            AI-Native Product Engineering
          </span>
        </motion.div>

        {/* H1 */}
        <h1
          data-testid={TID.heroH1}
          className="font-headings font-black tracking-[-0.03em] leading-[0.95] text-white text-[42px] sm:text-6xl md:text-7xl lg:text-[104px]"
        >
          <span className="block overflow-hidden">
            <motion.span
              className="inline-flex flex-wrap gap-x-4 md:gap-x-6"
              initial="hidden"
              animate="show"
            >
              {line1.map((w, i) => (
                <motion.span key={i} custom={i} variants={wordReveal} className="inline-block">
                  {w}
                </motion.span>
              ))}
            </motion.span>
          </span>
          <span className="block overflow-hidden mt-1 md:mt-2">
            <motion.span
              className="inline-flex flex-wrap gap-x-4 md:gap-x-6"
              initial="hidden"
              animate="show"
            >
              {line2.map((w, i) => (
                <motion.span
                  key={i}
                  custom={i + line1.length}
                  variants={wordReveal}
                  className={`inline-block ${w === "Without" ? "text-[#FF5F1F] italic font-black" : ""}`}
                >
                  {w}
                </motion.span>
              ))}
            </motion.span>
          </span>
        </h1>

        {/* Supporting copy */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 md:mt-12 max-w-2xl text-base md:text-lg leading-relaxed text-zinc-400"
        >
          Techovarya helps SaaS companies, technology businesses, and growing enterprises build
          intelligent products, modernize complex platforms, and accelerate engineering delivery.
          From AI agents and RAG systems to scalable SaaS platforms and dedicated product teams,
          we turn ambitious product challenges into production-ready software.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid={TID.heroCtaPrimary}
            onClick={() => scrollTo("contact")}
            className="group inline-flex items-center justify-center gap-2 bg-[#FF5F1F] hover:bg-[#E04F15] text-black font-bold px-7 py-4 text-sm md:text-base transition-all"
          >
            Book a Product Strategy Call
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid={TID.heroCtaSecondary}
            onClick={() => scrollTo("work")}
            className="group inline-flex items-center justify-center gap-2 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] text-white font-medium px-7 py-4 text-sm md:text-base transition-colors"
          >
            Explore Our Work
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>

        {/* Microcopy */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-6 text-xs md:text-sm text-zinc-500 max-w-lg font-mono"
        >
          No generic sales pitch. Start with a focused conversation about your product,
          architecture, or engineering challenge.
        </motion.p>
      </div>

      {/* Decorative corner mark */}
      <div className="absolute bottom-6 right-6 hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        <div className="w-3 h-3 border border-[#FF5F1F]" />
        <span>Engineering Reality — Scroll ↓</span>
      </div>
    </section>
  );
};
